import { cn } from '@/lib/utils';

interface ScoreBarProps {
  score: number;
  variant?: 'full' | 'compact' | 'mini';
  className?: string;
  /** Waarmee een toets déze balk aanwijst; zonder is hij niet te onderscheiden van andere. */
  'data-testid'?: string;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--success-dark)';
  if (score >= 50) return 'var(--warning)';
  return 'var(--destructive)';
}

function getScoreBgColor(score: number): string {
  if (score >= 80) return 'var(--success-light)';
  if (score >= 50) return 'var(--warning-light)';
  return 'var(--destructive-light)';
}

function getScoreIcon(score: number): string {
  if (score >= 80) return 'check_circle';
  if (score >= 50) return 'warning';
  return 'error';
}

export function ScoreBar({ score, variant = 'full', className, 'data-testid': testId = 'score-bar' }: ScoreBarProps) {
  const color = getScoreColor(score);
  const rounded = Math.round(score);

  if (variant === 'mini') {
    return (
      <span
        data-testid={testId}
        className={cn('inline-flex items-center gap-1 text-xs font-semibold', className)}
        style={{ color }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: '14px' }}
          aria-hidden="true"
        >
          {getScoreIcon(score)}
        </span>
        {rounded}%
      </span>
    );
  }

  return (
    <div data-testid={testId} className={cn('flex items-center gap-2', className)}>
      {/* Progress bar */}
      <div
        className="relative h-2 flex-1 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={rounded}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{ backgroundColor: getScoreBgColor(score) }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${score}%`,
            backgroundColor: color,
          }}
        />
      </div>

      {/* Percentage */}
      <span
        className="min-w-[2.5rem] text-right text-xs font-semibold"
        style={{ color }}
      >
        {rounded}%
      </span>

      {/* Icon (full variant only) */}
      {variant === 'full' && (
        <span
          className="material-symbols-outlined"
          style={{ fontSize: '16px', color }}
          aria-hidden="true"
        >
          {getScoreIcon(score)}
        </span>
      )}
    </div>
  );
}
